import React, { useCallback, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ApiService from '@common/services/ApiService';
import { DefaultModal } from '@common/components/default-modal';
import { DefaultButton } from '@common/components/default-button';
import { withStyles } from 'tss-react/mui';

import { unitResponsesStyles } from './styles';
import VcmUnitCheckModalMissingUnitDetail from './VcmUnitCheckModalMissingUnitDetail';
import VcmUnitCheckModalExtraUnitDetail from './VcmUnitCheckModalExtraUnitDetail';

const api = new ApiService();

const VcmUnitCheckModal = ({
  classes,
  isOpened,
  onClose,
  onRefresh,
  vcm,
  missingUnits,
  extraRemovedUnits,
  extraPresentUnits,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [rejectMissingUnitCodes, setRejectMissingUnitCodes] = useState([]);
  const [removeMissingUnitCodes, setRemoveMissingUnitCodes] = useState([]);
  const [keepExtraUnitCodes, setKeepExtraUnitCodes] = useState([]);
  const [removeExtraUnitCodes, setRemoveExtraUnitCodes] = useState([]);

  const combinedUnitCodes =
    missingUnits.map(u => u.code)
    .concat((extraRemovedUnits || []).map(u => u.code))
    .concat((extraPresentUnits || []).map(u => u.code));

  const setErrors = useCallback(
    (json) => {
      if (json.code == 1) {
        setErrorMessage(json.message);
      } else if (json.code == 2) {
        console.error(json);
        setErrorMessage('Unexpected error, please contact developers');
      }
    },
    [setErrorMessage]
  );

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    setRejectMissingUnitCodes([]);
    setRemoveMissingUnitCodes([]);
    setKeepExtraUnitCodes([]);
    setRemoveExtraUnitCodes([]);
    onClose();
  }, [setErrorMessage, setRejectMissingUnitCodes, setRemoveMissingUnitCodes, setKeepExtraUnitCodes, setRemoveExtraUnitCodes, onClose]);

  const handleSubmit = useCallback(async () => {
    const handleResponse = (res) => {
      if (res.status === 200) {
        return true;
      } else if (res.status === 422 || res.status === 400) {
        setErrors(res.json);
        return false;
      } else {
        console.error('Unknown response', res);
        return false;
      }
    };

    setIsSubmitting(true);

    try {
      if (rejectMissingUnitCodes.length) {
        const url1 = `/api/v1/vcms/${vcm.id}/bulk_reject_units`;
        const res1 = await api.put(url1, { unit_codes: rejectMissingUnitCodes });

        if (!handleResponse(res1)) {
          return;
        }
      }

      const removeUnitCodes = removeMissingUnitCodes.concat(removeExtraUnitCodes);

      if (removeUnitCodes.length) {
        const url2 = `/api/v1/vcms/${vcm.id}/bulk_remove_units`;
        const res2 = await api.put(url2, { unit_codes: removeUnitCodes });

        if (!handleResponse(res2)) {
          return;
        }
      }

      handleClose();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [rejectMissingUnitCodes, removeMissingUnitCodes, removeExtraUnitCodes, setErrors, setErrorMessage, setIsSubmitting, onRefresh, handleClose]);

  const transformUnitCodes = (currentUnitCodes, consideringUnitCode, enabledFunc) => {
    const index = currentUnitCodes.indexOf(consideringUnitCode);

    if (index != -1) {
      currentUnitCodes = currentUnitCodes.slice(0, index).concat(currentUnitCodes.slice(index + 1));
    }

    if (enabledFunc()) {
      return currentUnitCodes.concat([consideringUnitCode]);
    } else {
      return currentUnitCodes;
    }
  };

  const onMissingUnitCodeChange = useCallback(
    (unitCode, value) => {
      setRejectMissingUnitCodes(transformUnitCodes(rejectMissingUnitCodes, unitCode, () => value == 'reject'));
      setRemoveMissingUnitCodes(transformUnitCodes(removeMissingUnitCodes, unitCode, () => value == 'remove'));
    }, [rejectMissingUnitCodes, removeMissingUnitCodes, setRejectMissingUnitCodes, setRemoveMissingUnitCodes]
  );

  const onExtraUnitCodeChange = useCallback(
    (unitCode, value) => {
      setKeepExtraUnitCodes(transformUnitCodes(keepExtraUnitCodes, unitCode, () => value == 'keep'));
      setRemoveExtraUnitCodes(transformUnitCodes(removeExtraUnitCodes, unitCode, () => value == 'remove'));
    }, [keepExtraUnitCodes, removeExtraUnitCodes, setKeepExtraUnitCodes, setRemoveExtraUnitCodes]
  );

  var children = [];
  var selected = false;

  if (combinedUnitCodes.length) {
    children.push(
      <Typography>
        Units have been updated against the course record after this VCM was created.
        Please select an option for each unit below.
      </Typography>
    );
  }
  else {
    children.push(<Typography>This VCM has exactly the required units for its courses.</Typography>);
  }

  if (missingUnits.length) {
    children.push(
      <VcmUnitCheckModalMissingUnitDetail
        classes={classes}
        missingUnits={missingUnits}
        rejectUnitCodes={rejectMissingUnitCodes}
        removeUnitCodes={removeMissingUnitCodes}
        onCodeChange={onMissingUnitCodeChange}
      />
    );

    if (rejectMissingUnitCodes.length || removeMissingUnitCodes.length) {
      selected = true;
    }
  }

  if ((extraRemovedUnits && extraRemovedUnits.length) || (extraPresentUnits && extraPresentUnits.length)) {
    children.push(
      <VcmUnitCheckModalExtraUnitDetail
        classes={classes}
        extraRemovedUnits={extraRemovedUnits}
        extraPresentUnits={extraPresentUnits}
        keepUnitCodes={keepExtraUnitCodes}
        removeUnitCodes={removeExtraUnitCodes}
        onCodeChange={onExtraUnitCodeChange}
      />
    );

    if (keepExtraUnitCodes.length || removeExtraUnitCodes.length) {
      selected = true;
    }
  }

  return (
    <DefaultModal
      isOpened={isOpened}
      isLoading={isSubmitting}
      onClose={handleClose}
      title="VCM Unit Check"
      wide="1"
      action={
        <DefaultButton
          className={classes.button}
          color="secondary"
          onClick={combinedUnitCodes.length ? handleSubmit : handleClose}
          size="small"
          variant="contained"
          disabled={combinedUnitCodes.length && !selected}
        >
          {combinedUnitCodes.length ? 'Confirm' : 'Close'}
        </DefaultButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography color="error">{errorMessage}</Typography>
        </Grid>
        {children.map((child, index) =>
          <Grid key={index} size={12} mb={2}>
            {child}
          </Grid>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default withStyles(VcmUnitCheckModal, unitResponsesStyles, {
  withTheme: true
});
